import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { agentAxiosObj } from "../../../config/apiClients";
import { AgentProfile } from "../../../utils/types";
import { AgentProfileState } from "../../types";

const initialState: AgentProfileState = {
  profile: null,
  isLoading: false,
  isSavingDetails: false,
  isUploadingImage: false,
  loadError: null,
};

const rejectedMessage = (error: unknown) => (error as { message: string }).message;

export const getAgentProfile = createAsyncThunk<AgentProfile, void, { rejectValue: string }>(
  "agentProfile/get",
  async (_, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.get("/profile");
      return response.data.payload.profile;
    } catch (error) {
      return rejectWithValue(rejectedMessage(error));
    }
  },
);

export const updateAgentDetails = createAsyncThunk<
  AgentProfile,
  { phoneNumber: string; description: string },
  { rejectValue: string }
>(
  "agentProfile/updateDetails",
  async (details, { rejectWithValue }) => {
    try {
      const response = await agentAxiosObj.patch("/profile", details);
      return response.data.payload.profile;
    } catch (error) {
      return rejectWithValue(rejectedMessage(error));
    }
  },
);

export const uploadAgentProfileImage = createAsyncThunk<AgentProfile, File, { rejectValue: string }>(
  "agentProfile/uploadImage",
  async (image, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("myImage", image);
      const response = await agentAxiosObj.post("/profile/image", formData);
      return response.data.payload.profile;
    } catch (error) {
      return rejectWithValue(rejectedMessage(error));
    }
  },
);

const agentProfileSlice = createSlice({
  name: "agentProfile",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getAgentProfile.pending, (state) => {
        state.isLoading = true;
        state.loadError = null;
      })
      .addCase(getAgentProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
      })
      .addCase(getAgentProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.loadError = action.payload ?? "Failed to load profile";
      })
      .addCase(updateAgentDetails.pending, (state) => {
        state.isSavingDetails = true;
      })
      .addCase(updateAgentDetails.fulfilled, (state, action) => {
        state.isSavingDetails = false;
        state.profile = action.payload;
      })
      .addCase(updateAgentDetails.rejected, (state) => {
        state.isSavingDetails = false;
      })
      .addCase(uploadAgentProfileImage.pending, (state) => {
        state.isUploadingImage = true;
      })
      .addCase(uploadAgentProfileImage.fulfilled, (state, action) => {
        state.isUploadingImage = false;
        state.profile = action.payload;
      })
      .addCase(uploadAgentProfileImage.rejected, (state) => {
        state.isUploadingImage = false;
      });
  },
});

export default agentProfileSlice.reducer;
